import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import {
  getResourceSegment,
  getSearchParam,
  removeFromSearchParam,
  urlHasSearchParam,
} from '~/client/src/shared/utils/urlutils'

import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'
import BottomNavigationNavBar from '../BottomNavigationNavBar/BottomNavigationNavBar'
import MobileView from '../MobileView'
import { useMobileResourceActions } from './context'
import { MobileResource, MobileResourceConfig } from './index'

const URL_ACTION_PARAM = 'action'

interface MobileResourceViewLayoutProps {
  config: MobileResourceConfig
  state: MobileInitialState
  projectId?: string
  children?: React.ReactNode
}

const MobileResourceViewContent: React.FC<{
  projectId?: string
  children?: React.ReactNode
}> = ({ projectId, children }) => {
  const { selectRecord } = useMobileResourceActions()
  const stores = React.useContext(MobXProviderContext) as any
  const common: MobileCommonStore = stores.common
  const mountedPathRef = React.useRef(window.location.pathname)

  const runActionFromURL = React.useCallback(
    (transitionRoute?: string) => {
      if (transitionRoute) {
        try {
          const url = new URL(transitionRoute, window.location.origin)
          if (
            getResourceSegment(url.pathname) !==
            getResourceSegment(mountedPathRef.current)
          )
            return
        } catch {
          return
        }
      }

      if (urlHasSearchParam(URL_ACTION_PARAM)) {
        const action = getSearchParam(URL_ACTION_PARAM)
        if (action === 'create' && projectId) {
          selectRecord('new')
          removeFromSearchParam(URL_ACTION_PARAM, common.history)
        }
      }
    },
    [selectRecord, common.history, projectId],
  )

  React.useEffect(() => {
    runActionFromURL()
    common.addNavigationListener(runActionFromURL)
    return () => {
      common.removeNavigationListener(runActionFromURL)
    }
  }, [runActionFromURL, common])

  return (
    <>
      <MobileView
        header={
          <MobileResource.Header>
            <MobileResource.Title />
          </MobileResource.Header>
        }
        content={
          <MobileResource.Content>
            <MobileResource.FilterBar>
              <MobileResource.Search />
              <MobileResource.FilterButton />
              <MobileResource.ViewOptions />
              <MobileResource.ViewToggle />
            </MobileResource.FilterBar>
            <MobileResource.List />
          </MobileResource.Content>
        }
        footer={
          <BottomNavigationNavBar
            onCreateClicked={projectId ? () => selectRecord('new') : undefined}
          />
        }
      />
      <MobileResource.FilterPicker />
      {children}
    </>
  )
}

const MobileResourceViewLayout: React.FC<MobileResourceViewLayoutProps> = ({
  config,
  state,
  projectId,
  children,
}) => {
  return (
    <div className="tw:h-full">
      <MobileResource.Provider config={config} state={state}>
        <MobileResourceViewContent projectId={projectId}>
          {children}
        </MobileResourceViewContent>
      </MobileResource.Provider>
    </div>
  )
}

export default MobileResourceViewLayout
